import type {
  StateCreator,
  StoreApi,
  StoreMutatorIdentifier,
  UseBoundStore,
} from 'zustand';
import { devtools, type DevtoolsOptions } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import { isDevEnv } from 'shared/utils/environment/determineEnvironment';
import { isStoreDevtoolsEnabled } from 'shared/utils/environment/variables';

type AnyMutators = [StoreMutatorIdentifier, unknown][];
type StoreState<S> = S extends { getState: () => infer T } ? T : never;

type StoreWithSelectors<S extends UseBoundStore<StoreApi<object>>> = S & {
  use: {
    [K in keyof StoreState<S>]: () => StoreState<S>[K];
  };
};

export enum StoreName {
  Inspections = 'InspectionsStore',
  Companies = 'CompaniesStore',
  Contracts = 'ContractsStore',
  InspectionTemplates = 'InspectionTemplatesStore',
  Levels = 'LevelsStore',
  Sites = 'SitesStore',
  FieldValues = 'FieldValuesStore',
  Workflows = 'WorkflowsStore',
  Processes = 'ProcessesStore',
  IssueForm = 'IssueFormStore',
  VisibleFields = 'VisibleFieldsStore',
  Inspection = 'InspectionStore',
  Search = 'SearchStore',
  LevelsToSelect = 'LevelsToSelectStore',
  Issues = 'IssuesStore',
  IssueDocumentationOperations = 'IssueDocumentationOperationsStore',
  IssueParameters = 'IssueParametersStore',
  Documentation = 'DocumentationStore',
  Documents = 'DocumentsStore',
  FinancialData = 'FinancialDataStore',
  UploadProgress = 'UploadProgressStore',
  Analytics = 'AnalyticsStore',
  Project = 'ProjectStore',
  User = 'UserStore',
  ProjectUsers = 'ProjectUsersStore',
  Organizations = 'OrganizationsStore',
  Projects = 'ProjectsStore',
  Ui = 'UiStore',
}

export function withDevtools<
  S,
  Mos extends AnyMutators = [],
  Mis extends AnyMutators = [],
>(
  creator: StateCreator<S, Mos, Mis>,
  name: StoreName,
  options?: Omit<DevtoolsOptions, 'name'>
): StateCreator<S, Mos, Mis> {
  if (isDevEnv() && isStoreDevtoolsEnabled()) {
    return devtools(creator as any, {
      name,
      ...(options || {}),
    }) as StateCreator<S, Mos, Mis>;
  }
  return creator;
}

export function createSelectors<S extends UseBoundStore<StoreApi<object>>>(
  store: S
): StoreWithSelectors<S> {
  const storeWithSelectors = store as StoreWithSelectors<S>;
  storeWithSelectors.use = {} as StoreWithSelectors<S>['use'];

  for (const key of Object.keys(store.getState()) as Array<
    keyof StoreState<S>
  >) {
    storeWithSelectors.use[key] = () =>
      store(useShallow((state) => (state as StoreState<S>)[key]));
  }

  return storeWithSelectors;
}
